type JSONPrimitive = string | number | boolean | null;
export type LanguageJSON = {
  [K in keyof Language as Language[K] extends JSONPrimitive ? K : never]: string;
};

export class Language {
  constructor(
    public code: string,
    public language: string,
    public region: string,
    public paceText: string,
    public previewText: string,
  ) {}

  static fromJSON(json: LanguageJSON): Language {
    return new Language(json.code, json.language, json.region, json.paceText, json.previewText);
  }

  getBaseLanguageCode(): string {
    return this.code.split('-')[0];
  }

  getRegionCode(): string {
    return this.code.split('-')[1];
  }

  getDisplayCode(): string {
    // Special case for Expressive Multilingual - show "ML" instead of "ex"
    if (this.code === 'ex-ML') {
      return 'ML';
    }
    return this.getBaseLanguageCode();
  }

  // The offset to shift the ASCII code of each character in a region code string by
  // to derive the equivalent flag emoji for that code.
  private static EMOJI_CHARACTER_OFFSET = 127397;

  getRegionEmoji(): string | null {
    // Special case for Expressive Multilingual - return globe emoji
    if (this.code === 'ex-ML') {
      return '🌍';
    }

    // Flag emojis can be programmatically derived from ISO region codes!
    const regionCode = this.getRegionCode();
    if (!regionCode) {
      return null;
    }
    return String.fromCodePoint(
      ...regionCode
        .toUpperCase()
        .split('')
        .map((char) => char.charCodeAt(0) + Language.EMOJI_CHARACTER_OFFSET),
    );
  }

  getLabel(): string {
    return `${this.language}${this.region ? ` (${this.region})` : ''}`;
  }

  toJSON(): LanguageJSON {
    return {
      code: this.code,
      language: this.language,
      region: this.region,
      paceText: this.paceText,
      previewText: this.previewText,
    };
  }

  toJSONString(): string {
    return JSON.stringify(this.toJSON());
  }
}

const english_preview_text =
  "Hello there. I'm your friendly neighborhood ice cream fan, inviting you to stop by Waymark Creamery for their unbelievable flavors and homemade waffle cones.";
const french_preview_text =
  'Bonjour. Je suis votre fan de glace du quartier, vous invitant à passer chez Waymark Creamery pour découvrir leurs saveurs incroyables et leurs cornets de gaufre faits maison.';
const german_preview_text =
  'Hallo! Ich bin dein freundlicher Nachbarschafts-Eiscreme-Fan und lade dich ein, bei Waymark Creamery vorbeizuschauen, um ihre unglaublichen Geschmäcker und hausgemachten Waffelhörnchen zu probieren.';
const spanish_preview_text =
  'Hola. Soy tu fanático amigable de helados del vecindario, invitándote a pasar por Waymark Creamery para probar sus sabores increíbles y conos de waffle caseros.';
const brazilian_portuguese_preview_text =
  'Olá! Sou seu fã amigável de sorvete do bairro, convidando você a visitar a Waymark Creamery para experimentar seus sabores incríveis e casquinhas de waffle feitas à mão.';

const english_pace_text =
  "Discover the adventurous spirit of Waymark Creamery! Our artisanal ice cream is an ever-changing love letter to all the destinations we've explored and those still on our list. Every month, indulge in new, exciting flavors made with all-natural dairy and balanced butterfat for a clean finish. Join our journey and try the best ice cream anywhere, right here in the 313. Visit waymark creamery dot com or call 1-313-555-1212.";
const french_pace_text =
  "Découvrez l'esprit aventurier de Waymark Creamery ! Notre crème glacée artisanale est une déclaration d'amour en constante évolution à toutes les destinations que nous avons explorées et celles qui sont encore sur notre liste. Chaque mois, laissez-vous tenter par de nouvelles saveurs excitantes préparées avec des produits laitiers 100 % naturels et un juste équilibre de matières grasses pour une finition légère. Rejoignez notre voyage et essayez la meilleure crème glacée qui soit, ici même dans le 313. Visitez waymark creamery point com ou appelez le 1-313-555-1212.";
const german_pace_text =
  'Entdecke den abenteuerlichen Geist von Waymark Creamery! Unser handwerkliches Eis ist ein sich ständig wandelndes Liebesbrief an all die Reiseziele, die wir erkundet haben, und diejenigen, die noch auf unserer Liste stehen. Jeden Monat kannst du dich an neuen, aufregenden Geschmäckern erfreuen, die mit rein natürlichen Milchprodukten und ausgewogenem Butterfett für einen klaren Abgang zubereitet werden. Begleite uns auf unserer Reise und probiere das beste Eis überall, direkt hier im 313. Besuche waymark creamery Punkt com oder ruf 1-313-555-1212 an.';
const spanish_pace_text =
  '¡Descubre el espíritu aventurero de Waymark Creamery! Nuestro helado artesanal es una carta de amor en constante cambio a todos los destinos que hemos explorado y a los que aún están en nuestra lista. Cada mes, disfruta de nuevos y emocionantes sabores hechos con productos lácteos 100% naturales y una grasa equilibrada para un acabado limpio. Únete a nuestro viaje y prueba el mejor helado en cualquier lugar, justo aquí en el 313. Visita waymark creamery punto com o llama al 1-313-555-1212.';
const brazilian_portuguese_pace_text =
  'Descubra o espírito aventureiro da Waymark Creamery! Nosso sorvete artesanal é uma carta de amor em constante mudança para todos os destinos que exploramos e aqueles que ainda estão na nossa lista. Todo mês, delicie-se com novos e empolgantes sabores feitos com laticínios 100% naturais e um equilíbrio de gordura para um acabamento suave. Junte-se à nossa jornada e experimente o melhor sorvete de qualquer lugar, bem aqui no 313. Visite waymark creamery ponto com ou ligue para 1-313-555-1212.';

export const DefaultLanguages: Record<string, Language> = {
  'en-AU': new Language('en-AU', 'English', 'Australia', english_pace_text, english_preview_text),
  'en-CA': new Language('en-CA', 'English', 'Canada', english_pace_text, english_preview_text),
  'en-GB': new Language(
    'en-GB',
    'English',
    'United Kingdom',
    english_pace_text,
    english_preview_text,
  ),
  'en-IN': new Language('en-IN', 'English', 'India', english_pace_text, english_preview_text),
  'en-NZ': new Language('en-NZ', 'English', 'New Zealand', english_pace_text, english_preview_text),
  'en-US': new Language(
    'en-US',
    'English',
    'United States',
    english_pace_text,
    english_preview_text,
  ),
  'ex-ML': new Language(
    'ex-ML',
    'Expressive Multilingual',
    '',
    english_pace_text,
    english_preview_text,
  ),
  'es-MX': new Language('es-MX', 'Spanish', 'Mexico', spanish_pace_text, spanish_preview_text),
  es: new Language('es', 'Spanish', '', spanish_pace_text, spanish_preview_text),
  'fr-CA': new Language('fr-CA', 'French', 'Canada', french_pace_text, french_preview_text),
  'fr-FR': new Language('fr-FR', 'French', 'France', french_pace_text, french_preview_text),
  de: new Language('de', 'German', 'Germany', german_pace_text, german_preview_text),
  'pt-BR': new Language(
    'pt-BR',
    'Portuguese',
    'Brazil',
    brazilian_portuguese_pace_text,
    brazilian_portuguese_preview_text,
  ),
};

export const DEFAULT_LANGUAGE = DefaultLanguages['en-US'];
export const DEFAULT_LANGUAGE_CODE = DEFAULT_LANGUAGE.code;
